import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Heartbeat, Database, Lightning, ChatCircleDots, WhatsappLogo, ShieldCheck,
  CircleNotch, ArrowClockwise, Cpu, Clock, Warning, CheckCircle, X,
  ChartLineUp, Robot,
} from "@phosphor-icons/react";

const STATUS_THEME = {
  healthy:  { dot: "bg-emerald-500",  text: "text-emerald-400", ring: "border-emerald-500/30", bg: "bg-emerald-500/10",   label: "Saudável" },
  degraded: { dot: "bg-amber-400",    text: "text-amber-400",   ring: "border-amber-400/30",   bg: "bg-amber-400/10",     label: "Degradado" },
  critical: { dot: "bg-rose-500",     text: "text-rose-400",    ring: "border-rose-500/30",    bg: "bg-rose-500/10",      label: "Crítico" },
  unknown:  { dot: "bg-slate-500",    text: "text-slate-400",   ring: "border-slate-500/30",   bg: "bg-slate-500/10",     label: "Desconhecido" },
};

function fmtUptime(sec) {
  if (!sec) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const min = (Date.now() - new Date(iso).getTime()) / 60000;
  if (min < 1) return "agora";
  if (min < 60) return `${Math.round(min)}m atrás`;
  const h = min / 60;
  if (h < 24) return `${Math.round(h)}h atrás`;
  return `${Math.round(h / 24)}d atrás`;
}

export default function AdminInfra() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // /healthz is mounted at /api/healthz (no auth required)
      const baseUrl = api.defaults.baseURL.replace(/\/$/, "");
      const res = await fetch(`${baseUrl}/healthz`);
      const json = await res.json();
      setData(json);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  if (!data) {
    return <div className="px-4 md:px-8 py-14 md:py-16 text-center"><CircleNotch size={20} className="animate-spin text-slate-500 inline" /></div>;
  }

  const overall = STATUS_THEME[data.overall_status] || STATUS_THEME.unknown;

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-7">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Plataforma · Infra</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1 flex items-center gap-2.5">
            <Heartbeat size={26} weight="regular" className={overall.text} /> Saúde do sistema
          </h1>
          <p className="text-sm text-slate-400 mt-1.5">
            Última verificação: {timeAgo(data.checked_at)} · auto-refresh 60s · compute {data.compute_ms}ms
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="infra-refresh-btn"
          className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors disabled:opacity-60"
        >
          {loading ? <CircleNotch size={12} className="animate-spin" /> : <ArrowClockwise size={12} />}
          Atualizar
        </button>
      </header>

      {/* Overall card */}
      <section
        data-testid="infra-overall"
        className={`relative overflow-hidden rounded-xl border ${overall.ring} bg-gradient-to-br from-slate-900/40 to-transparent p-6 mb-5`}
      >
        <div className={`absolute top-0 left-0 right-0 h-0.5 ${overall.dot}`} />
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${overall.bg} border ${overall.ring} ${data.overall_status === "critical" ? "animate-pulse" : ""}`}>
              {data.overall_status === "healthy" ? <CheckCircle size={26} weight="fill" className={overall.text} /> : <Warning size={26} weight="fill" className={overall.text} />}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Status geral</div>
              <div className={`font-heading text-3xl font-semibold ${overall.text}`}>{overall.label}</div>
              <div className="text-[12px] text-slate-400 mt-0.5">
                v{data.version?.app_version || "dev"} · {data.version?.env || "—"} · uptime {fmtUptime(data.uptime_seconds)}
              </div>
            </div>
          </div>

          {data.problems?.length > 0 && (
            <div className="bg-white/[0.04] border border-white/5 rounded-lg p-4 max-w-md">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2">Problemas detectados</div>
              <ul className="space-y-1">
                {data.problems.map((p, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-200">
                    <span className={`shrink-0 ${overall.text}`}>•</span> {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Probes grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        <ProbeCard icon={Database} title="MongoDB" status={data.infra?.mongo?.status} testId="probe-mongo">
          <Row k="Ping"   v={`${data.infra?.mongo?.ping_ms ?? "—"} ms`} />
          <Row k="Database" v={data.infra?.mongo?.db || "—"} />
          {data.infra?.mongo?.error && <Row k="Erro" v={data.infra.mongo.error} danger />}
        </ProbeCard>

        <ProbeCard icon={Cpu} title="Host" status={data.infra?.host?.available ? "healthy" : "unknown"} testId="probe-host">
          <Row k="Memória RSS" v={data.infra?.host?.memory_rss_mb != null ? `${data.infra.host.memory_rss_mb} MB` : "—"} />
          <Row k="CPU"          v={data.infra?.host?.cpu_percent != null ? `${data.infra.host.cpu_percent}%` : "—"} />
          <Row k="Mem sistema"  v={data.infra?.host?.system_memory_percent != null ? `${data.infra.host.system_memory_percent}%` : "—"} />
        </ProbeCard>

        <ProbeCard icon={Clock} title="Scheduler" status={data.scheduler?.status} testId="probe-scheduler">
          <Row k="Jobs ativos" v={data.scheduler?.jobs_total} />
          <Row k="Online"      v={data.scheduler?.running ? "Sim" : "Não"} danger={!data.scheduler?.running} />
          {data.scheduler?.issues?.length > 0 && (
            <div className="text-[11px] text-amber-400 mt-2">
              {data.scheduler.issues.slice(0, 3).map((i, idx) => <div key={idx}>• {i}</div>)}
            </div>
          )}
        </ProbeCard>

        <ProbeCard icon={WhatsappLogo} title="WhatsApp / Z-API" status={data.whatsapp?.status} testId="probe-whatsapp">
          <Row k="Z-API"        v={data.whatsapp?.enabled ? "ON" : "OFF"} />
          {data.whatsapp?.enabled && (
            <>
              <Row k="Reachability" v={data.whatsapp?.reachability_ms != null ? `${data.whatsapp.reachability_ms}ms (${data.whatsapp.reachability_status || "—"})` : "—"} />
              <Row k="Smartphone"   v={data.whatsapp?.smartphone_connected === true ? "Conectado" : data.whatsapp?.smartphone_connected === false ? "Desconectado" : "—"} />
              <Row k="Sent 24h"     v={data.whatsapp?.sent_24h ?? 0} />
              <Row k="Failed 24h"   v={data.whatsapp?.failed_24h ?? 0} danger={(data.whatsapp?.failed_24h || 0) > 0} />
              <Row k="Fail rate"    v={`${((data.whatsapp?.fail_rate_24h || 0) * 100).toFixed(1)}%`} />
              <Row k="Last inbound" v={timeAgo(data.whatsapp?.last_inbound_at)} />
            </>
          )}
        </ProbeCard>

        <ProbeCard icon={Robot} title="LLM" status={data.llm?.status} testId="probe-llm">
          <Row k="Provider"   v={data.llm?.provider || "—"} />
          <Row k="Calls 24h"  v={data.llm?.calls_24h ?? 0} />
          <Row k="Errors 24h" v={data.llm?.errors_24h ?? 0} danger={(data.llm?.errors_24h || 0) > 0} />
          <Row k="Error rate" v={`${((data.llm?.error_rate_24h || 0) * 100).toFixed(1)}%`} />
        </ProbeCard>

        <ProbeCard icon={ChatCircleDots} title="Webhooks" status={data.webhooks?.status} testId="probe-webhooks">
          <Row k="Inbound 24h"        v={data.webhooks?.inbound_24h ?? 0} />
          <Row k="Inbound errors 24h" v={data.webhooks?.inbound_errors_24h ?? 0} danger={(data.webhooks?.inbound_errors_24h || 0) > 0} />
          <Row k="Outbound errors 24h" v={data.webhooks?.outbound_errors_24h ?? 0} danger={(data.webhooks?.outbound_errors_24h || 0) > 0} />
        </ProbeCard>

        <ProbeCard icon={ShieldCheck} title="Health Transitions" status={data.health_transitions?.status} testId="probe-transitions">
          <Row k="Último scan"    v={timeAgo(data.health_transitions?.last_scan_at)} />
          <Row k="Status"         v={data.health_transitions?.last_scan_status || "—"} danger={data.health_transitions?.last_scan_status === "error"} />
          <Row k="Alertas 24h"    v={data.health_transitions?.alerts_24h ?? 0} />
          <Row k="Críticos 24h"   v={data.health_transitions?.alerts_critical_24h ?? 0} danger={(data.health_transitions?.alerts_critical_24h || 0) > 0} />
          <Row k="Último alerta"  v={timeAgo(data.health_transitions?.last_alert_at)} />
        </ProbeCard>
      </div>

      {/* Scheduler jobs detail */}
      <section className="mt-5 bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
        <header className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
          <Lightning size={14} weight="regular" className="text-amber-400" />
          <h2 className="font-heading text-[14px] font-semibold text-white">Jobs do scheduler</h2>
          <span className="text-[11px] text-slate-500 ml-auto">{data.scheduler?.jobs?.length || 0} registrados</span>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-[12px]" data-testid="scheduler-jobs-table">
            <thead className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500 border-b border-white/5">
              <tr>
                <th className="px-5 py-2 font-medium">Job</th>
                <th className="px-5 py-2 font-medium">Status</th>
                <th className="px-5 py-2 font-medium">Última execução</th>
                <th className="px-5 py-2 font-medium">Próxima</th>
                <th className="px-5 py-2 font-medium text-right">Runs</th>
                <th className="px-5 py-2 font-medium text-right">Erros</th>
              </tr>
            </thead>
            <tbody>
              {data.scheduler?.jobs?.map((j) => {
                const ok = j.last_status === "ok";
                return (
                  <tr key={j.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-2.5">
                      <div className="text-white font-medium">{j.label}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{j.id}</div>
                    </td>
                    <td className="px-5 py-2.5">
                      {j.last_status ? (
                        <span className={`inline-flex items-center gap-1 text-[11px] ${ok ? "text-emerald-400" : "text-rose-400"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-rose-500"}`} />
                          {j.last_status}
                          {j.late && <span className="ml-1 text-amber-400">· atrasado</span>}
                        </span>
                      ) : (
                        <span className="text-slate-500 text-[11px]">aguardando</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-slate-400">{timeAgo(j.last_run_at)}</td>
                    <td className="px-5 py-2.5 text-slate-400">{j.next_run_at ? timeAgo(j.next_run_at).replace("atrás", "") : "—"}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-slate-300">{j.total_runs}</td>
                    <td className={`px-5 py-2.5 text-right tabular-nums ${j.total_errors > 0 ? "text-rose-400" : "text-slate-500"}`}>{j.total_errors}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* v0.99.8 · Fev/2026 — Seed self-service GOV */}
      <GovSeedCard />
    </div>
  );
}

function GovSeedCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const trigger = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const { data } = await api.post("/admin/seed/gov-users");
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Falha desconhecida");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="gov-seed-card"
      className="mt-5 rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/20 to-transparent p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-400/70 font-medium mb-1">
            Deploy Ops · GOV
          </div>
          <h3 className="font-heading text-[16px] font-semibold text-white mb-1.5 flex items-center gap-2">
            <ShieldCheck size={18} weight="regular" className="text-emerald-400" />
            Restaurar usuários GOV
          </h3>
          <p className="text-[12.5px] text-slate-400 leading-relaxed max-w-[560px]">
            Cria/reconcilia os dois tenants institucionais (<code className="text-emerald-300">gov-piloto-osasco</code> e <code className="text-emerald-300">gov-demo-santa-aurora</code>) e reseta as senhas dos owners para os valores documentados. Idempotente — pode rodar quantas vezes precisar.
          </p>
        </div>
        <button
          onClick={trigger}
          disabled={busy}
          data-testid="gov-seed-trigger"
          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 border border-emerald-400 px-4 py-2 rounded-md text-[13px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? <CircleNotch size={13} className="animate-spin" /> : <Lightning size={13} weight="bold" />}
          {busy ? "Provisionando…" : "Restaurar agora"}
        </button>
      </div>

      {err && (
        <div className="mt-4 rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-300 text-[12px] px-3 py-2 font-mono">
          {err}
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-2" data-testid="gov-seed-result">
          <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-medium">
            ✅ Provisionado com sucesso
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {(result.credentials || []).map((c) => (
              <div
                key={c.email}
                className="rounded-md border border-white/10 bg-white/[0.03] p-3 text-[12px]"
                data-testid={`gov-cred-${c.email}`}
              >
                <div className="text-white font-semibold">{c.role}</div>
                <div className="text-slate-300 font-mono text-[11.5px] mt-1 space-y-0.5">
                  <div><span className="text-slate-500">email:</span> {c.email}</div>
                  <div><span className="text-slate-500">senha:</span> {c.password}</div>
                  <div><span className="text-slate-500">url:</span> {c.url}</div>
                </div>
              </div>
            ))}
          </div>
          <details className="text-[11px] text-slate-500 mt-2">
            <summary className="cursor-pointer hover:text-slate-300">Ver payload completo</summary>
            <pre className="mt-2 text-[10.5px] bg-black/40 border border-white/5 rounded p-2 overflow-x-auto">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </section>
  );
}

function ProbeCard({ icon: Icon, title, status, children, testId }) {
  const t = STATUS_THEME[status] || STATUS_THEME.unknown;
  return (
    <div data-testid={testId} className={`relative bg-white/[0.03] border ${t.ring} rounded-xl p-5 overflow-hidden`}>
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${t.dot}`} />
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon size={16} weight="regular" className="text-slate-400" />
          <h3 className="font-heading text-[14px] font-semibold text-white">{title}</h3>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${t.bg} ${t.text}`}>
          <span className={`w-1 h-1 rounded-full ${t.dot}`} /> {t.label}
        </span>
      </header>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}

function Row({ k, v, danger }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[12px]">
      <dt className="text-slate-500 truncate">{k}</dt>
      <dd className={`tabular-nums font-medium ${danger ? "text-rose-400" : "text-slate-200"}`}>{v}</dd>
    </div>
  );
}
